import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  clearStoredUser,
  fetchJson,
  getAuthEventName,
  persistUser,
  readStoredUser,
  refreshStoredUser,
} from '../utils/auth';

const cardStyle = {
  width: 'min(560px, 92vw)',
  margin: '40px auto',
  padding: '32px',
  border: '2px solid rgba(243, 229, 171, 0.45)',
  borderRadius: '18px',
  background: 'rgba(24, 13, 7, 0.82)',
  boxShadow: '0 18px 40px rgba(0, 0, 0, 0.45)',
};

const inputStyle = {
  width: '100%',
  padding: '12px 14px',
  borderRadius: '10px',
  border: '1px solid rgba(243, 229, 171, 0.35)',
  background: 'rgba(255, 248, 220, 0.1)',
  color: '#F3E5AB',
  fontSize: '16px',
  fontFamily: 'Georgia, serif',
  boxSizing: 'border-box',
};

const buttonStyle = {
  width: '100%',
  padding: '12px 14px',
  borderRadius: '10px',
  border: 'none',
  background: 'linear-gradient(135deg, #8B6B2E, #B08A45)',
  color: '#20130A',
  cursor: 'pointer',
  fontWeight: 700,
  fontSize: '16px',
  fontFamily: 'Milonga, serif',
};

const secondaryButtonStyle = {
  ...buttonStyle,
  background: 'rgba(243, 229, 171, 0.14)',
  color: '#F3E5AB',
  border: '1px solid rgba(243, 229, 171, 0.28)',
};

const linkButtonStyle = {
  background: 'none',
  border: 'none',
  color: '#F3E5AB',
  cursor: 'pointer',
  fontFamily: 'Milonga, serif',
  fontSize: '16px',
  padding: 0,
  textDecoration: 'underline',
};

const tabButtonStyle = {
  flex: 1,
  padding: '12px 14px',
  borderRadius: '10px',
  border: '1px solid rgba(243, 229, 171, 0.28)',
  background: 'rgba(243, 229, 171, 0.08)',
  color: '#F3E5AB',
  cursor: 'pointer',
  fontWeight: 700,
  fontSize: '16px',
  fontFamily: 'Milonga, serif',
};

function Message({ type, text }) {
  if (!text) {
    return null;
  }

  return (
    <div
      style={{
        padding: '12px 14px',
        borderRadius: '10px',
        marginBottom: '18px',
        whiteSpace: 'pre-line',
        background: type === 'error' ? 'rgba(126, 27, 27, 0.45)' : 'rgba(52, 92, 42, 0.45)',
        border: `1px solid ${type === 'error' ? 'rgba(255, 162, 162, 0.45)' : 'rgba(179, 238, 162, 0.45)'}`,
      }}
    >
      {text}
    </div>
  );
}

function buildMessage(response, payload) {
  if (!response.ok && payload.details) {
    return `${payload.message || 'Request failed.'}\n${payload.details}`;
  }

  return payload.message || 'Request completed.';
}

function formatRole(role = 'user') {
  const normalizedRole = String(role).trim().toLowerCase();

  if (normalizedRole === 'owner') {
    return 'Owner';
  }

  if (normalizedRole === 'admin') {
    return 'Admin';
  }

  if (normalizedRole === 'traveler') {
    return 'Traveler';
  }

  return 'User';
}

export default function Login() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mode, setMode] = useState('login');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [, setDeleteToken] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const [isBusy, setIsBusy] = useState(false);
  const [user, setUser] = useState(() => readStoredUser());
  const [accountUsername, setAccountUsername] = useState(() => readStoredUser()?.username || '');
  const [activeTab, setActiveTab] = useState('profile');
  const [patrons, setPatrons] = useState([]);
  const [isPatronsBusy, setIsPatronsBusy] = useState(false);
  const canManagePatrons = user?.role === 'owner' || user?.role === 'admin';

  useEffect(() => {
    const syncUser = () => {
      const storedUser = readStoredUser();
      setUser(storedUser);
      setAccountUsername(storedUser?.username || '');

      if (storedUser?.role !== 'owner' && storedUser?.role !== 'admin') {
        setActiveTab('profile');
      }
    };

    window.addEventListener('storage', syncUser);
    window.addEventListener(getAuthEventName(), syncUser);

    return () => {
      window.removeEventListener('storage', syncUser);
      window.removeEventListener(getAuthEventName(), syncUser);
    };
  }, []);

  useEffect(() => {
    let ignore = false;

    async function syncLatestAccount() {
      try {
        const refreshedUser = await refreshStoredUser();

        if (!ignore && refreshedUser) {
          setUser(refreshedUser);
          setAccountUsername(refreshedUser.username || '');
        }
      } catch (_error) {
        if (!ignore) {
          const storedUser = readStoredUser();
          setUser(storedUser);
          setAccountUsername(storedUser?.username || '');
        }
      }
    }

    syncLatestAccount();

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    if (user?.role !== 'owner' && user?.role !== 'admin') {
      setPatrons([]);
    }
  }, [user]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const verifyToken = params.get('verifyToken');
    const resetTokenParam = params.get('resetToken');
    const deleteTokenParam = params.get('deleteToken');
    const emailParam = params.get('email');

    if (deleteTokenParam && emailParam) {
      let ignore = false;

      async function deleteAccount() {
        setIsBusy(true);

        try {
          const { response, payload } = await fetchJson('/auth/delete-account', {
            method: 'POST',
            body: JSON.stringify({
              email: emailParam,
              token: deleteTokenParam,
            }),
          });
          if (ignore) {
            return;
          }

          clearStoredUser();
          setUser(null);
          setAccountUsername('');
          setMode('login');
          setEmail(emailParam);
          setDeleteToken(deleteTokenParam);
          setMessageType(response.ok ? 'success' : 'error');
          setMessage(buildMessage(response, payload));
        } catch (error) {
          if (!ignore) {
            setMessageType('error');
            setMessage('Unable to delete your account right now.');
          }
        } finally {
          if (!ignore) {
            setIsBusy(false);
          }
        }
      }

      deleteAccount();

      return () => {
        ignore = true;
      };
    }

    if (resetTokenParam && emailParam) {
      setMode('reset');
      setEmail(emailParam);
      setResetToken(resetTokenParam);
      setDeleteToken('');
      setPassword('');
      setConfirmPassword('');
      setMessage('Choose a new password for your account.');
      setMessageType('success');
      return;
    }

    if (!verifyToken || !emailParam) {
      return;
    }

    let ignore = false;

    async function verifyEmailAddress() {
      setIsBusy(true);

      try {
        const { response, payload } = await fetchJson('/auth/verify-email', {
          method: 'POST',
          body: JSON.stringify({
            email: emailParam,
            token: verifyToken,
          }),
        });
        if (ignore) {
          return;
        }

        setMode('login');
        setMessageType(response.ok ? 'success' : 'error');
        setMessage(buildMessage(response, payload));
        setEmail(emailParam);
      } catch (error) {
        if (!ignore) {
          setMessageType('error');
          setMessage('Unable to verify email right now.');
        }
      } finally {
        if (!ignore) {
          setIsBusy(false);
        }
      }
    }

    verifyEmailAddress();

    return () => {
      ignore = true;
    };
  }, [location.search]);

  function switchMode(nextMode) {
    setMode(nextMode);
    setUsername('');
    setPassword('');
    setConfirmPassword('');
    setResetToken('');
    setDeleteToken('');
    setMessage('');
    setMessageType('success');
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if ((mode === 'register' || mode === 'reset') && password !== confirmPassword) {
      setMessageType('error');
      setMessage('Passwords must match.');
      return;
    }

    setIsBusy(true);
    setMessage('');

    const endpointMap = {
      login: '/auth/login',
      register: '/auth/register',
      forgot: '/auth/forgot-password',
      reset: '/auth/reset-password',
    };

    const body = { email };

    if (mode === 'login' || mode === 'register') {
      body.password = password;
    }

    if (mode === 'register') {
      body.username = username;
    }

    if (mode === 'reset') {
      body.password = password;
      body.token = resetToken;
    }

    if (mode === 'register' || mode === 'reset') {
      body.confirmPassword = confirmPassword;
    }

    try {
      const { response, payload } = await fetchJson(endpointMap[mode], {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setMessageType(response.ok ? 'success' : 'error');
      setMessage(buildMessage(response, payload));

      if (response.ok && mode === 'login' && payload.user) {
        setUser(payload.user);
        setAccountUsername(payload.user.username || '');
        persistUser(payload.user);
        navigate('/');
      }

      if (response.ok && mode === 'register') {
        setMode('login');
        setUsername('');
        setPassword('');
        setConfirmPassword('');
      }

      if (response.ok && mode === 'forgot') {
        setMode('login');
      }

      if (response.ok && mode === 'reset') {
        setMode('login');
        setPassword('');
        setConfirmPassword('');
        setResetToken('');
      }
    } catch (error) {
      setMessageType('error');
      setMessage('Unable to reach the server right now.');
    } finally {
      setIsBusy(false);
    }
  }

  async function handleAccountUpdate(event) {
    event.preventDefault();
    setIsBusy(true);
    setMessage('');

    try {
      const { response, payload } = await fetchJson('/auth/update-profile', {
        method: 'POST',
        body: JSON.stringify({
          username: accountUsername,
        }),
      });
      setMessageType(response.ok ? 'success' : 'error');
      setMessage(buildMessage(response, payload));

      if (response.ok && payload.user) {
        setUser(payload.user);
        persistUser(payload.user);
      }
    } catch (error) {
      setMessageType('error');
      setMessage('Unable to reach the server right now.');
    } finally {
      setIsBusy(false);
    }
  }

  async function handleDeleteRequest() {
    setIsBusy(true);
    setMessage('');

    try {
      const { response, payload } = await fetchJson('/auth/request-delete-account', {
        method: 'POST',
        body: JSON.stringify({}),
      });
      setMessageType(response.ok ? 'success' : 'error');
      setMessage(buildMessage(response, payload));
    } catch (error) {
      setMessageType('error');
      setMessage('Unable to reach the server right now.');
    } finally {
      setIsBusy(false);
    }
  }

  async function handlePasswordChangeRequest() {
    setIsBusy(true);
    setMessage('');

    try {
      const { response, payload } = await fetchJson('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({
          email: user.email,
        }),
      });
      setMessageType(response.ok ? 'success' : 'error');
      setMessage(buildMessage(response, payload));
    } catch (error) {
      setMessageType('error');
      setMessage('Unable to reach the server right now.');
    } finally {
      setIsBusy(false);
    }
  }

  async function logout() {
    setIsBusy(true);

    try {
      const { response, payload } = await fetchJson('/auth/logout', {
        method: 'POST',
        body: JSON.stringify({}),
      });

      clearStoredUser();
      setUser(null);
      setAccountUsername('');
      setActiveTab('profile');
      setPatrons([]);
      setMessageType(response.ok ? 'success' : 'error');
      setMessage(buildMessage(response, payload));
    } catch (_error) {
      clearStoredUser();
      setUser(null);
      setAccountUsername('');
      setActiveTab('profile');
      setPatrons([]);
      setMessageType('error');
      setMessage('Signed out locally, but the server could not be reached to clear the server session.');
    } finally {
      setIsBusy(false);
    }
  }

  async function loadPatrons() {
    if (!canManagePatrons) {
      return;
    }

    setIsPatronsBusy(true);

    try {
      const { response, payload } = await fetchJson('/auth/patrons', {
        method: 'POST',
        body: JSON.stringify({}),
      });
      setMessageType(response.ok ? 'success' : 'error');

      if (!response.ok) {
        setMessage(buildMessage(response, payload));
        setPatrons([]);
        return;
      }

      setPatrons(payload.patrons || []);
      setMessage('');
    } catch (error) {
      setMessageType('error');
      setMessage('Unable to reach the server right now.');
      setPatrons([]);
    } finally {
      setIsPatronsBusy(false);
    }
  }

  async function handleRoleChange(patron, nextRole) {
    if (!canManagePatrons || !patron?.canToggleAdmin) {
      return;
    }

    setIsPatronsBusy(true);
    setMessage('');

    try {
      const { response, payload } = await fetchJson('/auth/patrons/set-role', {
        method: 'POST',
        body: JSON.stringify({
          targetUserId: patron.id,
          role: nextRole,
        }),
      });
      setMessageType(response.ok ? 'success' : 'error');
      setMessage(buildMessage(response, payload));

      if (response.ok && payload.patron) {
        setPatrons((currentPatrons) =>
          currentPatrons.map((currentPatron) =>
            currentPatron.id === payload.patron.id ? payload.patron : currentPatron
          )
        );
      }
    } catch (error) {
      setMessageType('error');
      setMessage('Unable to reach the server right now.');
    } finally {
      setIsPatronsBusy(false);
    }
  }

  function getSubmitLabel() {
    if (isBusy) {
      return 'Please wait...';
    }

    if (mode === 'register') {
      return 'Create Account';
    }

    if (mode === 'forgot') {
      return 'Send Reset Email';
    }

    if (mode === 'reset') {
      return 'Update Password';
    }

    return 'Sign In';
  }

  return (
    <div
      style={{
        minHeight: 'calc(100vh - 220px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '30px 16px 60px',
        color: '#F3E5AB',
        fontFamily: 'Milonga, serif',
      }}
    >
      <div style={cardStyle}>
        <h1 style={{ marginTop: 0, fontSize: '42px', textAlign: 'center' }}>
          {user ? 'Welcome' : 'Traveler Login'}
        </h1>

        <Message type={messageType} text={message} />

        {user ? (
          <div style={{ lineHeight: 1.7, fontSize: '18px' }}>
            <p style={{ textAlign: 'center' }}>Signed in as {user.email}</p>
            <p style={{ textAlign: 'center' }}>User Type: {formatRole(user.role)}</p>
            <p style={{ textAlign: 'center' }}>
              Last login recorded: {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : 'N/A'}
            </p>

            <button
              type="button"
              style={{ ...secondaryButtonStyle, marginTop: '14px' }}
              onClick={logout}
            >
              Log Out
            </button>

            <hr style={{ margin: '28px 0' }} />

            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
              <button
                type="button"
                onClick={() => setActiveTab('profile')}
                style={{
                  ...tabButtonStyle,
                  background: activeTab === 'profile' ? 'linear-gradient(135deg, #8B6B2E, #B08A45)' : tabButtonStyle.background,
                  color: activeTab === 'profile' ? '#20130A' : tabButtonStyle.color,
                }}
              >
                Manage Account
              </button>
              {canManagePatrons && (
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab('patrons');
                    loadPatrons();
                  }}
                  style={{
                    ...tabButtonStyle,
                    background: activeTab === 'patrons' ? 'linear-gradient(135deg, #8B6B2E, #B08A45)' : tabButtonStyle.background,
                    color: activeTab === 'patrons' ? '#20130A' : tabButtonStyle.color,
                  }}
                >
                  Patrons
                </button>
              )}
            </div>

            {activeTab === 'profile' ? (
              <>
                <form onSubmit={handleAccountUpdate}>
                  <label style={{ display: 'block', textAlign: 'center', marginBottom: '8px', fontSize: '24px' }}>
                    Manage Account
                  </label>

                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '18px' }}>Change Username</label>
                  <input
                    type="text"
                    value={accountUsername}
                    onChange={(event) => setAccountUsername(event.target.value)}
                    style={inputStyle}
                    minLength={3}
                    maxLength={24}
                    required
                  />

                  <button type="submit" style={{ ...buttonStyle, marginTop: '22px' }} disabled={isBusy}>
                    {isBusy ? 'Please wait...' : 'Save Username'}
                  </button>
                </form>

                <div style={{ marginTop: '18px', textAlign: 'center' }}>
                  <button type="button" style={linkButtonStyle} onClick={handlePasswordChangeRequest} disabled={isBusy}>
                    Change Password
                  </button>
                </div>

                <div style={{ marginTop: '18px', textAlign: 'center' }}>
                  <button type="button" style={linkButtonStyle} onClick={handleDeleteRequest} disabled={isBusy}>
                    Delete Account
                  </button>
                </div>
              </>
            ) : (
              <div>
                <label style={{ display: 'block', textAlign: 'center', marginBottom: '8px', fontSize: '24px' }}>
                  Patron Roles
                </label>
                <p style={{ textAlign: 'center', marginTop: 0 }}>
                  {user.role === 'owner'
                    ? 'Owners can assign User, Traveler, or Admin roles.'
                    : 'Admins can assign User or Traveler roles.'}
                </p>

                <button
                  type="button"
                  style={{ ...secondaryButtonStyle, marginBottom: '18px' }}
                  onClick={loadPatrons}
                  disabled={isPatronsBusy}
                >
                  {isPatronsBusy ? 'Refreshing...' : 'Refresh Patrons'}
                </button>

                <div style={{ display: 'grid', gap: '14px' }}>
                  {patrons.length === 0 ? (
                    <div
                      style={{
                        border: '1px solid rgba(243, 229, 171, 0.2)',
                        borderRadius: '12px',
                        padding: '18px',
                        textAlign: 'center',
                        background: 'rgba(255, 248, 220, 0.05)',
                      }}
                    >
                      {isPatronsBusy ? 'Loading patrons...' : 'No patrons found yet.'}
                    </div>
                  ) : (
                    patrons.map((patron) => (
                      <div
                        key={patron.id}
                        style={{
                          border: '1px solid rgba(243, 229, 171, 0.2)',
                          borderRadius: '12px',
                          padding: '16px',
                          background: 'rgba(255, 248, 220, 0.05)',
                        }}
                      >
                        <div style={{ fontSize: '20px' }}>{patron.username || 'Unnamed user'}</div>
                        <div style={{ fontSize: '16px', opacity: 0.88 }}>{patron.email}</div>
                        <div style={{ fontSize: '16px' }}>Type: {formatRole(patron.role)}</div>
                        <div style={{ fontSize: '16px' }}>Verified: {patron.isVerified ? 'Yes' : 'No'}</div>
                        <div style={{ fontSize: '16px' }}>
                          Last login: {patron.lastLoginAt ? new Date(patron.lastLoginAt).toLocaleString() : 'N/A'}
                        </div>
                        <div style={{ fontSize: '16px' }}>
                          Joined: {patron.createdAt ? new Date(patron.createdAt).toLocaleString() : 'N/A'}
                        </div>

                        {patron.canToggleAdmin ? (
                          <div style={{ display: 'grid', gap: '10px', marginTop: '14px' }}>
                            <button
                              type="button"
                              style={buttonStyle}
                              onClick={() => handleRoleChange(patron, 'user')}
                              disabled={
                                isPatronsBusy ||
                                patron.role === 'user' ||
                                (user.role === 'admin' && patron.role === 'admin')
                              }
                            >
                              {isPatronsBusy && patron.role !== 'user' ? 'Please wait...' : 'Make User'}
                            </button>
                            <button
                              type="button"
                              style={buttonStyle}
                              onClick={() => handleRoleChange(patron, 'traveler')}
                              disabled={
                                isPatronsBusy ||
                                patron.role === 'traveler' ||
                                (user.role === 'admin' && patron.role === 'admin')
                              }
                            >
                              {isPatronsBusy && patron.role !== 'traveler' ? 'Please wait...' : 'Make Traveler'}
                            </button>
                            {user.role === 'owner' && (
                              <button
                                type="button"
                                style={buttonStyle}
                                onClick={() => handleRoleChange(patron, 'admin')}
                                disabled={isPatronsBusy || patron.role === 'admin'}
                              >
                                {isPatronsBusy && patron.role !== 'admin' ? 'Please wait...' : 'Make Admin'}
                              </button>
                            )}
                          </div>
                        ) : (
                          <div style={{ marginTop: '12px', fontSize: '16px', opacity: 0.88 }}>
                            Owner access is fixed here and cannot be changed.
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '12px',
                marginBottom: '20px',
              }}
            >
              <button
                type="button"
                onClick={() => switchMode('login')}
                style={{
                  ...buttonStyle,
                  opacity: mode === 'login' ? 1 : 0.65,
                }}
              >
                Login
              </button>
              <button
                type="button"
                onClick={() => switchMode('register')}
                style={{
                  ...buttonStyle,
                  opacity: mode === 'register' ? 1 : 0.65,
                }}
              >
                Register
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {mode === 'register' && (
                <>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '18px' }}>Username</label>
                  <input
                    type="text"
                    value={username}
                    onChange={(event) => setUsername(event.target.value)}
                    style={inputStyle}
                    autoComplete="username"
                    minLength={3}
                    maxLength={24}
                    required
                  />
                </>
              )}

              <label style={{ display: 'block', margin: mode === 'register' ? '18px 0 8px' : '0 0 8px', fontSize: '18px' }}>
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                style={inputStyle}
                autoComplete="email"
                required
              />

              {mode !== 'forgot' && (
                <>
                  <label style={{ display: 'block', margin: '18px 0 8px', fontSize: '18px' }}>Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    style={inputStyle}
                    autoComplete={mode === 'register' || mode === 'reset' ? 'new-password' : 'current-password'}
                    minLength={8}
                    required
                  />
                </>
              )}

              {(mode === 'register' || mode === 'reset') && (
                <>
                  <label style={{ display: 'block', margin: '18px 0 8px', fontSize: '18px' }}>
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    style={inputStyle}
                    autoComplete="new-password"
                    minLength={8}
                    required
                  />
                </>
              )}

              <button type="submit" style={{ ...buttonStyle, marginTop: '22px' }} disabled={isBusy}>
                {getSubmitLabel()}
              </button>
            </form>

            <div style={{ marginTop: '18px', textAlign: 'center', lineHeight: 1.8 }}>
              {mode === 'login' && (
                <button type="button" style={linkButtonStyle} onClick={() => switchMode('forgot')}>
                  Forgot password?
                </button>
              )}

              {mode === 'forgot' && (
                <button type="button" style={linkButtonStyle} onClick={() => switchMode('login')}>
                  Back to login
                </button>
              )}

              {mode === 'reset' && (
                <button type="button" style={linkButtonStyle} onClick={() => switchMode('login')}>
                  Cancel reset
                </button>
              )}

              {mode === 'register' && (
                <p>After registering, open the verification link sent to your email before logging in.</p>
              )}

              {mode === 'login' && <p>Only verified accounts can sign in.</p>}

              {mode === 'forgot' && <p>Enter your email and we will send you a password reset link.</p>}

              {mode === 'reset' && <p>Choose a new password, then sign in with it.</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
